import { errorToast } from "@/v2-ui/toast"
import {
  ButtonHint,
  ConfirmButton,
  type ConfirmButtonProps,
} from "@/v2-backoffice-client/components/button"
import { useNavLayerEnableMutation } from "@/v2-backoffice-client/navlayer/enable/navlayer.enable.mutation"
import type { NavLayerFragment } from "@/v2-backoffice-client/navlayer/__types__/navlayer.fragment"

export type NavLayerEnableButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm"
> & {
  navLayer: NavLayerFragment
}

/**
 * Publishes or withdraws a nav layer. Heavier than it looks: nav layers are
 * the geometry the map is navigated by, so withdrawing the one a country
 * relies on leaves users there unable to select a property at all.
 */
function NavLayerEnableButton(props: NavLayerEnableButtonProps) {
  const { navLayer, children, ...buttonProps } = props

  const [toggle, { loading, error }] = useNavLayerEnableMutation()
  errorToast(error, { title: "Failed to update the nav layer" })

  const isEnabled = navLayer.IsEnabled

  return (
    <ConfirmButton
      variant={isEnabled ? "warning" : "success"}
      confirmVariant={isEnabled ? "warning" : "success"}
      isStacked
      size="md"
      iconSrc={isEnabled ? "eye-crossed" : "eye"}
      isLoading={loading}
      content={
        isEnabled ? (
          <ButtonHint title="Disable — stop serving this geometry">
            Takes this nav layer off the map for every customer in this
            environment. Where it is the only one for a country, users there can
            no longer select a property.
          </ButtonHint>
        ) : (
          <ButtonHint title="Go live — show to customers in this environment">
            Serves this nav layer on the map right away, to every customer the
            country rules allow.
          </ButtonHint>
        )
      }
      confirmContent={
        isEnabled
          ? "Withdraw this nav layer from all customers?"
          : "Show this nav layer to all customers?"
      }
      onConfirm={() => {
        toggle({
          variables: {
            featureCode: navLayer.FeatureCode,
            isEnabled: !isEnabled,
          },
        })
      }}
      {...buttonProps}
    >
      {children ?? (isEnabled ? "Take offline" : "Go live")}
    </ConfirmButton>
  )
}

export default NavLayerEnableButton
